import React, { useState } from 'react';
import { useCapabilitySuggestions, useExtractCapabilities, useCoachingInsights } from '@/hooks/useAI';
import { useWorkspaceStore } from '@/stores/workspaceStore';
import type { AIResultItem } from '@/types';

// ─── Result item ──────────────────────────────────────────────────────────────

const ResultItem: React.FC<{ item: AIResultItem; onAction?: () => void }> = ({ item, onAction }) => (
  <div className="p-3 rounded-lg border border-surface-200 dark:border-surface-800 bg-surface-50 dark:bg-surface-800/50 space-y-1.5">
    <div className="flex items-start justify-between gap-2">
      <h4 className="text-xs font-semibold text-surface-900 dark:text-white leading-snug flex-1">
        {item.title}
      </h4>
      <span className="text-2xs font-bold text-accent-600 dark:text-accent-400 flex-shrink-0">
        {Math.round(item.score * 100)}%
      </span>
    </div>
    <p className="text-xs text-surface-500 leading-relaxed">{item.summary}</p>
    {item.reasoning && (
      <p className="text-2xs text-primary-600 dark:text-primary-400 italic">
        {item.reasoning}
      </p>
    )}
    {item.suggestedAction && (
      <button
        type="button"
        onClick={onAction}
        className="inline-flex items-center gap-1 text-2xs font-medium text-primary-700 dark:text-primary-300 hover:underline"
      >
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path d="m5 12 7-7 7 7" /><path d="M12 19V5" />
        </svg>
        {item.suggestedAction}
      </button>
    )}
    {item.tags.length > 0 && (
      <div className="flex flex-wrap gap-1">
        {item.tags.map((tag) => (
          <span key={tag} className="px-1.5 py-0.5 rounded text-2xs bg-surface-200 dark:bg-surface-700 text-surface-500">
            {tag}
          </span>
        ))}
      </div>
    )}
  </div>
);

// ─── Loading skeleton ─────────────────────────────────────────────────────────

const LoadingSkeleton: React.FC<{ count?: number }> = ({ count = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="p-3 rounded-lg border border-surface-200 dark:border-surface-800 space-y-2 animate-pulse">
        <div className="h-3 bg-surface-200 dark:bg-surface-700 rounded w-3/4" />
        <div className="h-2 bg-surface-200 dark:bg-surface-700 rounded" />
        <div className="h-2 bg-surface-200 dark:bg-surface-700 rounded w-2/3" />
      </div>
    ))}
  </div>
);

// ─── AIPanel ──────────────────────────────────────────────────────────────────

type PanelTab = 'suggestions' | 'extract' | 'coaching';

interface AIPanelProps {
  workspaceId: string;
  capabilityId: string;
  onClose: () => void;
}

const AIPanel: React.FC<AIPanelProps> = ({ workspaceId, capabilityId, onClose }) => {
  const [activeTab, setActiveTab] = useState<PanelTab>('suggestions');
  const [extractText, setExtractText] = useState('');

  const capability = useWorkspaceStore((s) => s.capabilities[capabilityId]);

  const {
    data: suggestions,
    isLoading: suggestionsLoading,
    error: suggestionsError,
    refetch: refetchSuggestions,
  } = useCapabilitySuggestions(workspaceId, capabilityId);

  const {
    data: coaching,
    isLoading: coachingLoading,
    error: coachingError,
  } = useCoachingInsights(workspaceId, [capabilityId]);

  const extractMutation = useExtractCapabilities(workspaceId);

  const tabs: { id: PanelTab; label: string }[] = [
    { id: 'suggestions', label: 'Suggestions' },
    { id: 'extract', label: 'Extract' },
    { id: 'coaching', label: 'Coaching' },
  ];

  return (
    <aside className="w-80 flex-shrink-0 border-l border-surface-200 dark:border-surface-800 bg-white dark:bg-surface-900 flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-surface-200 dark:border-surface-800">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-md bg-primary-700 flex items-center justify-center">
            <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 2a10 10 0 1 0 10 10" /><path d="M22 2 12 12" /><path d="m17 2 5 5-5 5" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-semibold text-surface-900 dark:text-white">AI Assistant</h3>
            {capability && (
              <p className="text-2xs text-surface-400 truncate max-w-40">{capability.title}</p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1 rounded hover:bg-surface-100 dark:hover:bg-surface-800 text-surface-400 transition-colors"
          aria-label="Close AI panel"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-surface-200 dark:border-surface-800">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={[
              'flex-1 px-2 py-2 text-xs font-medium transition-colors',
              activeTab === tab.id
                ? 'border-b-2 border-primary-600 text-primary-700 dark:text-primary-300'
                : 'text-surface-500 hover:text-surface-700 dark:hover:text-surface-300',
            ].join(' ')}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {activeTab === 'suggestions' && (
          <>
            <div className="flex items-center justify-between">
              <p className="text-xs text-surface-500">
                AI-recommended capabilities to explore
              </p>
              <button
                type="button"
                onClick={() => refetchSuggestions()}
                className="p-1 rounded text-surface-400 hover:text-surface-600 hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
                title="Refresh suggestions"
              >
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                  <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                </svg>
              </button>
            </div>

            {suggestionsLoading ? (
              <LoadingSkeleton />
            ) : suggestionsError ? (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400">
                Failed to load suggestions. Check your AI configuration.
              </div>
            ) : !suggestions || suggestions.results.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-xs text-surface-400">No suggestions yet</p>
                <button
                  type="button"
                  onClick={() => refetchSuggestions()}
                  className="mt-2 text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Generate now
                </button>
              </div>
            ) : (
              suggestions.results.map((item) => (
                <ResultItem key={item.id} item={item} />
              ))
            )}
          </>
        )}

        {activeTab === 'extract' && (
          <>
            <p className="text-xs text-surface-500">
              Paste text (resume, bio, job description) and let AI extract capabilities
            </p>
            <textarea
              value={extractText}
              onChange={(e) => setExtractText(e.target.value)}
              placeholder="Paste text here…"
              rows={6}
              className="w-full rounded-lg border border-surface-300 dark:border-surface-600 bg-white dark:bg-surface-900 text-xs px-3 py-2.5 outline-none focus:border-primary-500 resize-none"
            />
            <button
              type="button"
              onClick={() => {
                if (extractText.trim()) {
                  extractMutation.mutate(extractText);
                }
              }}
              disabled={extractMutation.isPending || !extractText.trim()}
              className="w-full py-2 text-xs font-medium text-white bg-primary-700 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-60"
            >
              {extractMutation.isPending ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Extracting…
                </span>
              ) : (
                'Extract capabilities'
              )}
            </button>

            {extractMutation.data && (
              <div className="space-y-2">
                <p className="text-2xs font-semibold text-surface-500 uppercase tracking-wide">
                  Found {extractMutation.data.results.length} capabilities
                </p>
                {extractMutation.data.results.map((item) => (
                  <ResultItem key={item.id} item={item} />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'coaching' && (
          <>
            <p className="text-xs text-surface-500">
              Personalised coaching insights based on your capability profile
            </p>

            {coachingLoading ? (
              <LoadingSkeleton count={2} />
            ) : coachingError ? (
              <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-xs text-red-600 dark:text-red-400">
                Failed to load coaching. Check your AI configuration.
              </div>
            ) : !coaching || coaching.results.length === 0 ? (
              <div className="text-center py-8 text-xs text-surface-400">
                No coaching insights available
              </div>
            ) : (
              coaching.results.map((item) => (
                <ResultItem key={item.id} item={item} />
              ))
            )}
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-surface-200 dark:border-surface-800">
        <p className="text-2xs text-surface-400 text-center">
          Powered by AI · Results may vary
        </p>
      </div>
    </aside>
  );
};

export default AIPanel;
